// 일정 관리(v3.26) — 전체 화면 오버레이 패널: 월간 캘린더 + 목록 + 등록/수정/삭제.
// 설계문서: 소스/doc/schedule-management-design.md (v1.2)
// - 보기/등록: 웹채팅 로그인 사용자 전원. 수정/삭제: 등록자 본인 + 관리자(can_edit).
// - 동시간대 중복(Zoom 라이선스 초과) 검사 없음 — 캘린더에서 눈으로 확인해 조율(확정).
import { useCallback, useEffect, useState } from "react";
import { api, ForbiddenError, UnauthorizedError } from "@/api/client";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/cn";
import type { ScheduleItem, ScheduleMeta } from "@/api/types";
import MonthCalendar from "./MonthCalendar";
import ScheduleList from "./ScheduleList";
import ScheduleFormModal from "./ScheduleFormModal";
import { dayKey, fieldBadgeClass, fmtDateTimeRange, gridRangeIso } from "./utils";
import { ChevronLeft, ChevronRight, Loader2, Plus, Repeat, X } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  /** 홈 '다가오는 일정'에서 넘어온 일정 — 해당 월로 이동해 상세를 바로 펼친다. */
  initialItem?: ScheduleItem | null;
  /** 등록/수정/삭제 직후 호출 — 홈 위젯·헤더 배지를 최신화하려고 Chat 이 넘긴다. */
  onChanged?: () => void;
}

function firstOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export default function SchedulePanel({ open, onClose, initialItem, onChanged }: Props) {
  const [monthDate, setMonthDate] = useState<Date>(() => firstOfMonth(new Date()));
  const [meta, setMeta] = useState<ScheduleMeta | null>(null);
  const [items, setItems] = useState<ScheduleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [catFilter, setCatFilter] = useState("");
  const [fieldFilter, setFieldFilter] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [detail, setDetail] = useState<ScheduleItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduleItem | null>(null);
  // 반복 일정 삭제 범위 선택 다이얼로그(이 일정만 / 반복 전체 / 취소) 대상
  const [deleteTarget, setDeleteTarget] = useState<ScheduleItem | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = gridRangeIso(monthDate);
      const r = await api.schedules(from, to, catFilter || undefined, fieldFilter || undefined);
      setItems(r.items);
    } catch (e) {
      if (e instanceof UnauthorizedError) toast("로그인이 필요합니다.", "error");
      else if (e instanceof ForbiddenError) toast(e.message, "error");
      else toast("일정을 불러오지 못했습니다.", "error");
    } finally {
      setLoading(false);
    }
  }, [monthDate, catFilter, fieldFilter]);

  // 열릴 때: meta 1회 + 목록 조회. 월/필터 변경 시 재조회.
  useEffect(() => {
    if (!open) return;
    if (!meta) api.scheduleMeta().then(setMeta).catch(() => { /* 폼에서 폴백 사용 */ });
    reload();
  }, [open, reload, meta]);

  // 홈에서 특정 일정을 눌러 열었으면 그 달로 이동하고 상세를 펼친다.
  // (목록은 이미 초기 렌더에서 아이템을 들고 있으므로 조회 완료를 기다릴 필요가 없다.)
  useEffect(() => {
    if (!open || !initialItem) return;
    const start = new Date(initialItem.start_at);
    setMonthDate(firstOfMonth(start));
    setSelectedKey(dayKey(start));
    setDetail(initialItem);
  }, [open, initialItem]);

  // ESC 로 닫기(모달·폼이 떠 있으면 그것부터 닫힘)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (formOpen) setFormOpen(false);
      else if (detail) setDetail(null);
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, formOpen, detail, onClose]);

  if (!open) return null;

  const moveMonth = (delta: number) => {
    setSelectedKey(null);
    setMonthDate((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1));
  };
  const goToday = () => {
    setMonthDate(firstOfMonth(new Date()));
    setSelectedKey(null);
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (s: ScheduleItem) => {
    setDetail(null);
    setEditing(s);
    setFormOpen(true);
  };
  const doDelete = async (s: ScheduleItem, scope: "one" | "series") => {
    try {
      await api.scheduleDelete(s.id, scope);
      toast(scope === "series" ? "반복 일정 전체를 삭제했습니다." : "일정을 삭제했습니다.", "success");
      setDeleteTarget(null);
      setDetail(null);
      reload();
      onChanged?.();
    } catch (e) {
      if (e instanceof ForbiddenError) toast(e.message, "error");
      else toast("삭제에 실패했습니다.", "error");
    }
  };

  const onDelete = (s: ScheduleItem) => {
    // 반복 일정이면 범위 선택 다이얼로그(이 일정만/전체), 단건이면 기존 확인창
    if (s.repeat_group) {
      setDeleteTarget(s);
      return;
    }
    if (!window.confirm(`"${s.title}" 일정을 삭제할까요? 삭제하면 되돌릴 수 없습니다.`)) return;
    doDelete(s, "one");
  };

  const selCls =
    "rounded-lg border border-border bg-card px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-primary/40";

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-background">
      {/* 헤더 */}
      {/* pt 는 safe-area 와 기본 패딩 중 큰 값 — env() 가 0인 데스크톱에서 pt-[env(...)] 가
          padding-top 을 0으로 덮어 버튼이 상단에 붙던 문제 수정 */}
      <div className="flex items-center gap-2 border-b border-border px-3 py-2.5 pt-[max(env(safe-area-inset-top),0.625rem)]">
        <span className="font-bold">일정</span>
        <span className="hidden text-xs text-muted-foreground sm:inline">
          Zoom 라이선스 2개 — 겹치는 시간은 서로 조율해 주세요
        </span>
        <div className="flex-1" />
        <button
          onClick={openCreate}
          className="flex items-center gap-1 rounded-lg bg-[#0E1E2D] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#1C3954]"
        >
          <Plus className="h-4 w-4" /> 일정 추가
        </button>
        <button onClick={onClose} aria-label="닫기" className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* 월 이동 + 필터 */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
        <div className="flex items-center gap-1">
          <button onClick={() => moveMonth(-1)} aria-label="이전 달" className="rounded-lg p-1 text-muted-foreground hover:bg-muted">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="min-w-[110px] text-center text-base font-bold">
            {monthDate.getFullYear()}년 {monthDate.getMonth() + 1}월
          </span>
          <button onClick={() => moveMonth(1)} aria-label="다음 달" className="rounded-lg p-1 text-muted-foreground hover:bg-muted">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        <button onClick={goToday} className="rounded-lg border border-border px-2.5 py-1 text-xs hover:bg-muted">
          오늘
        </button>
        <div className="flex-1" />
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className={selCls} aria-label="카테고리 필터">
          <option value="">카테고리: 모두</option>
          {(meta?.categories ?? []).map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select value={fieldFilter} onChange={(e) => setFieldFilter(e.target.value)} className={selCls} aria-label="분야 필터">
          <option value="">분야: 모두</option>
          {(meta?.fields ?? []).map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </div>

      {/* 본문: 캘린더 + 목록 */}
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        <div className="mx-auto max-w-3xl">
          {loading && items.length === 0 ? (
            <div className="flex justify-center py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <>
              <MonthCalendar
                monthDate={monthDate}
                schedules={items}
                selectedKey={selectedKey}
                onSelectDate={(k) => setSelectedKey((prev) => (prev === k ? null : k))}
              />
              <div className="mt-3">
                <ScheduleList items={items} selectedKey={selectedKey} onOpen={setDetail} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* 상세 모달 */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDetail(null)}>
          <div className="w-full max-w-md rounded-xl bg-card p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-start justify-between gap-2">
              <h3 className="text-base font-bold">{detail.title}</h3>
              <button onClick={() => setDetail(null)} aria-label="닫기" className="rounded-md p-1 text-muted-foreground hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-1.5">
                <span className="rounded-full border border-foreground/60 px-2 py-0.5 text-[11px] font-semibold">
                  {detail.category}
                </span>
                <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", fieldBadgeClass(detail.field))}>
                  {detail.field}
                </span>
                {detail.repeat_group && (
                  <span className="flex items-center gap-0.5 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                    <Repeat className="h-3 w-3" /> 반복
                  </span>
                )}
              </div>
              <p className="tabular-nums">{fmtDateTimeRange(detail)}</p>
              {detail.location && (
                <p><span className="text-muted-foreground">장소</span> · {detail.location}</p>
              )}
              {detail.memo && (
                <p className="whitespace-pre-wrap rounded-lg bg-muted p-2 text-[13px]">{detail.memo}</p>
              )}
              <p className="border-t border-border pt-2 text-xs text-muted-foreground">
                등록 {detail.created_by_name || detail.created_by_email}
                {" · "}
                {detail.created_at.slice(0, 10)}
              </p>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              {detail.can_edit && (
                <>
                  <button
                    onClick={() => openEdit(detail)}
                    className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-muted"
                  >
                    수정
                  </button>
                  <button
                    onClick={() => onDelete(detail)}
                    className="rounded-lg border border-destructive px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10"
                  >
                    삭제
                  </button>
                </>
              )}
              <button
                onClick={() => setDetail(null)}
                className="rounded-lg bg-[#0E1E2D] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#1C3954]"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 반복 일정 삭제 범위 선택 — 이 일정만 / 반복 전체 / 취소 */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={() => setDeleteTarget(null)}>
          <div className="w-full max-w-sm rounded-xl bg-card p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1 flex items-center gap-1.5 font-semibold">
              <Repeat className="h-4 w-4 text-muted-foreground" /> 반복 일정 삭제
            </div>
            <p className="mb-1 text-sm">"{deleteTarget.title}" 은(는) 반복 일정입니다.</p>
            <p className="mb-3 text-xs text-muted-foreground">
              이 회차만 삭제할지, 반복 전체를 삭제할지 선택해 주세요. 삭제하면 되돌릴 수 없습니다.
            </p>
            <div className="flex flex-col gap-1.5">
              <button
                onClick={() => doDelete(deleteTarget, "one")}
                className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted"
              >
                이 일정만 삭제
              </button>
              <button
                onClick={() => doDelete(deleteTarget, "series")}
                className="rounded-lg border border-destructive px-3 py-2 text-sm font-semibold text-destructive hover:bg-destructive/10"
              >
                반복 전체 삭제
              </button>
              <button
                onClick={() => setDeleteTarget(null)}
                className="rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 등록/수정 폼 */}
      <ScheduleFormModal
        open={formOpen}
        meta={meta}
        editing={editing}
        defaultDayKey={selectedKey}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          reload();
          onChanged?.();
        }}
      />
    </div>
  );
}
